import {
  useApolloClient,
  useMutation as useApolloMutation
} from "@apollo/client/react/hooks"
import { useTranslation } from "react-i18next"

import { callMutation } from "shared/api/callMutation"
import { emitAppEvent } from "shared/lib/appEventEmitter"
import { useProjectRequestMonitor } from "shared/lib/request-monitoring/useProjectRequestMonitor"
import { useEvent } from "shared/lib/useEvent"
import { useRequestToasts } from "shared/lib/useRequestToasts"
import { ProductPositionTypename, ProjectTypename } from "shared/types/Project"
import {
  CREATE_FAVORITE,
  CREATE_PROJECT_CHILD
} from "shared/types/ProjectMutations"
import { ProductPositionAddon } from "shared/types/__generated__/graphql"
import { InvalidDataError } from "shared/types/error/InvalidDataError"

import { useProductConfigurationQueryWithCacheLazy } from "entities/dim-configuration/api/useProductConfigurationQueryWithCache"
import { getProductConfigurationQueryVariables } from "entities/dim-configuration/lib/getProductConfigurationQueryVariables"
import { getProductPositionAddonsFromConfiguration } from "entities/dim-configuration/lib/getProductPositionAddonsFromConfiguration"
import { FAVORITES_PROJECT_ID } from "entities/favorites/api/useFavoriteMutations"
import {
  CreateFavoriteDocument,
  CreateFavoriteMutation,
  CreateFavoriteMutationVariables
} from "entities/favorites/graphql/mutation.generated"
import { useFavoritesLazyQuery } from "entities/favorites/graphql/queries.generated"
import { getProductType } from "entities/products/lib/getProductType"
import { insertProjectChild } from "entities/project-children/lib/insertProjectChild"
import { useProjectStoreActions } from "features/project-management/model/store/useProjectStoreActions"

import { evictProductPosition } from "../lib/evictProductPosition"
import { prepareProductPositionCreate } from "../model/prepareProductPositionCreate"
import { CreateProductPositionParams } from "../types/ProductPosition"

export interface CreateFavoriteArgs {
  productPosition: CreateProductPositionParams
}

export function useCreateFavorite() {
  const { t } = useTranslation()
  const { cache: apolloCache } = useApolloClient()
  const [fetchFavorites] = useFavoritesLazyQuery()
  const [fetchConfiguration] = useProductConfigurationQueryWithCacheLazy()
  const { setElementPending, unsetElementPending } = useProjectStoreActions()

  const { raiseLoadingToast } = useRequestToasts({
    messages: {
      loading: t("Request.Favorite.CREATE_FAVORITE.Start"),
      success: t("Request.Favorite.CREATE_FAVORITE.Success"),
      failure: t("Request.Favorite.CREATE_FAVORITE.Failure")
    }
  })

  const startProjectRequest = useProjectRequestMonitor()

  const [apolloMutate, rest] = useApolloMutation<
    CreateFavoriteMutation,
    CreateFavoriteMutationVariables
  >(CreateFavoriteDocument, {
    update: (cache, result) => {
      if (result.data) {
        insertProjectChild({
          child: result.data.productPosition,
          position: result.data.productPosition.position,
          parentID: FAVORITES_PROJECT_ID,
          parentType: ProjectTypename,
          cache
        })
      }
    }
  })

  const createFavorite = useEvent(
    async ({ productPosition }: CreateFavoriteArgs) => {
      const { raiseSuccessToast, raiseErrorToast } = raiseLoadingToast()
      let addons: ProductPositionAddon[] = []
      if (productPosition.configurationKey) {
        const configuration = await fetchConfiguration({
          variables: getProductConfigurationQueryVariables({
            productType: getProductType(productPosition.variantKey),
            configurationKey: productPosition.configurationKey
          })
        })

        addons = getProductPositionAddonsFromConfiguration(
          configuration.data?.productConfiguration
        )
      }

      await fetchFavorites()

      const { input, optimisticResponse } = prepareProductPositionCreate({
        projectID: FAVORITES_PROJECT_ID,
        parentID: FAVORITES_PROJECT_ID,
        productPosition,
        addons,
        cache: apolloCache
      })

      if (!input) {
        const error = new InvalidDataError(
          "failed to prepare product position input",
          {
            data: productPosition
          }
        )
        error.notify()
        raiseErrorToast()
        return { data: null, errors: [{ message: error.message }] }
      }

      const completeProjectRequest = startProjectRequest({
        projectID: FAVORITES_PROJECT_ID,
        requestType: CREATE_FAVORITE
      })

      setElementPending({
        itemID: input.id,
        projectID: FAVORITES_PROJECT_ID
      })

      emitAppEvent(CREATE_PROJECT_CHILD, {
        id: input.id,
        projectID: FAVORITES_PROJECT_ID,
        parentID: FAVORITES_PROJECT_ID,
        parentType: ProjectTypename,
        type: ProductPositionTypename
      })

      return callMutation<
        CreateFavoriteMutation,
        CreateFavoriteMutationVariables,
        CreateFavoriteMutation["productPosition"]
      >(apolloMutate, {
        variables: { input } as CreateFavoriteMutationVariables,
        transform: data => data.productPosition,
        apolloOptions: {
          optimisticResponse,
          onCompleted: () => {
            unsetElementPending({
              itemID: input.id,
              projectID: FAVORITES_PROJECT_ID
            })

            completeProjectRequest()
            raiseSuccessToast()
          },
          onError: () => {
            evictProductPosition(input.id, apolloCache)

            unsetElementPending({
              itemID: input.id,
              projectID: FAVORITES_PROJECT_ID
            })

            completeProjectRequest()
            raiseErrorToast()
          }
        }
      })
    }
  )

  return [createFavorite, rest] as [typeof createFavorite, typeof rest]
}
